import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  TrendingUp,
  Search,
  Download,
  Plus,
  Edit,
  Trash2,
  Eye,
  Users,
  Filter,
  DollarSign,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import AddJobTitleModal from './components/AddJobTitleModal';
import AddGradeModal from './components/AddGradeModal';
import EditJobTitleModal from './components/EditJobTitleModal';
import EditGradeModal from './components/EditGradeModal';
import JobTitleDetailModal from './components/JobTitleDetailModal';
import GradeDetailModal from './components/GradeDetailModal';

// ---- Types ----
interface JobTitle {
  id: string;
  title: string;
  department: string;
  grade: string;
  employeeCount: number;
  status: string;
  description: string;
  requirements: string[];
  reportingTo: string;
  createdDate: string;
}

interface SalaryGrade {
  id: string;
  gradeName: string;
  level: string;
  salaryRange: { min: number; max: number; currency: string };
  progressionCriteria: string[];
  benefitsPackage: string;
  eligibilityRules: string;
  jobTitleCount: number;
  employeeCount: number;
  status: string;
  createdDate: string;
}

// ---- Mock Data ----
const mockJobTitles: JobTitle[] = [
  { id: 'JT001', title: 'Senior Software Engineer', department: 'Engineering', grade: 'Grade 12', employeeCount: 15, status: 'Active', description: 'Responsible for designing, developing, and maintaining complex software systems', requirements: ["Bachelor's degree in Computer Science", '5+ years of experience', 'Proficiency in React, Node.js'], reportingTo: 'Engineering Manager', createdDate: '2024-01-15' },
  { id: 'JT002', title: 'Product Manager', department: 'Product', grade: 'Grade 13', employeeCount: 8, status: 'Active', description: 'Lead product strategy and roadmap development', requirements: ['MBA or equivalent', '3+ years product management experience', 'Strong analytical skills'], reportingTo: 'VP of Product', createdDate: '2024-01-20' },
  { id: 'JT003', title: 'UX Designer', department: 'Design', grade: 'Grade 10', employeeCount: 6, status: 'Active', description: 'Create user-centered design solutions', requirements: ['Design degree or portfolio', '2+ years UX experience', 'Proficiency in Figma, Adobe Suite'], reportingTo: 'Design Lead', createdDate: '2024-02-01' },
  { id: 'JT004', title: 'HR Business Partner', department: 'Human Resources', grade: 'Grade 11', employeeCount: 4, status: 'Active', description: 'Support business units with HR strategies and initiatives', requirements: ["Bachelor's in HR or related field", '4+ years HR experience', 'SHRM certification preferred'], reportingTo: 'HR Director', createdDate: '2024-01-10' },
  { id: 'JT005', title: 'Marketing Specialist', department: 'Marketing', grade: 'Grade 9', employeeCount: 12, status: 'Active', description: 'Execute marketing campaigns and strategies', requirements: ['Marketing or Communications degree', '2+ years marketing experience', 'Digital marketing skills'], reportingTo: 'Marketing Manager', createdDate: '2024-02-15' },
  { id: 'JT006', title: 'Junior Developer', department: 'Engineering', grade: 'Grade 7', employeeCount: 10, status: 'Draft', description: 'Entry-level software development position', requirements: ['Computer Science degree', 'Internship experience preferred', 'Programming fundamentals'], reportingTo: 'Senior Software Engineer', createdDate: '2024-03-01' },
];

const mockSalaryGrades: SalaryGrade[] = [
  { id: 'GR007', gradeName: 'Grade 7', level: 'Entry Level', salaryRange: { min: 8000, max: 12000, currency: 'AED' }, progressionCriteria: ['6 months probation completion', 'Basic performance targets'], benefitsPackage: 'Basic Benefits', eligibilityRules: 'Fresh graduates or entry-level professionals', jobTitleCount: 3, employeeCount: 25, status: 'Active', createdDate: '2024-01-01' },
  { id: 'GR009', gradeName: 'Grade 9', level: 'Junior Level', salaryRange: { min: 12000, max: 18000, currency: 'AED' }, progressionCriteria: ['2 years experience', 'Meets performance standards', 'Skills assessment'], benefitsPackage: 'Standard Benefits', eligibilityRules: '2+ years relevant experience', jobTitleCount: 5, employeeCount: 32, status: 'Active', createdDate: '2024-01-01' },
  { id: 'GR010', gradeName: 'Grade 10', level: 'Mid Level', salaryRange: { min: 18000, max: 25000, currency: 'AED' }, progressionCriteria: ['3-4 years experience', 'Leadership potential', 'Advanced skills certification'], benefitsPackage: 'Enhanced Benefits', eligibilityRules: '3+ years relevant experience', jobTitleCount: 8, employeeCount: 28, status: 'Active', createdDate: '2024-01-01' },
  { id: 'GR011', gradeName: 'Grade 11', level: 'Senior Level', salaryRange: { min: 25000, max: 35000, currency: 'AED' }, progressionCriteria: ['5+ years experience', 'Team leadership', 'Project management skills'], benefitsPackage: 'Premium Benefits', eligibilityRules: '5+ years relevant experience with leadership experience', jobTitleCount: 6, employeeCount: 18, status: 'Active', createdDate: '2024-01-01' },
  { id: 'GR012', gradeName: 'Grade 12', level: 'Senior Level II', salaryRange: { min: 35000, max: 50000, currency: 'AED' }, progressionCriteria: ['7+ years experience', 'Strategic thinking', 'Department impact'], benefitsPackage: 'Executive Benefits', eligibilityRules: '7+ years experience with strategic responsibilities', jobTitleCount: 4, employeeCount: 12, status: 'Active', createdDate: '2024-01-01' },
  { id: 'GR013', gradeName: 'Grade 13', level: 'Management Level', salaryRange: { min: 50000, max: 75000, currency: 'AED' }, progressionCriteria: ['10+ years experience', 'Management excellence', 'Business impact'], benefitsPackage: 'Executive Plus Benefits', eligibilityRules: '10+ years with proven management track record', jobTitleCount: 3, employeeCount: 8, status: 'Active', createdDate: '2024-01-01' },
];

// ---- Main Component ----
export default function JobTitlesGrades() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'job-titles' | 'salary-grades'>('job-titles');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedGrade, setExpandedGrade] = useState<string | null>(null);

  // Modal state
  const [showAddJobTitle, setShowAddJobTitle] = useState(false);
  const [showAddGrade, setShowAddGrade] = useState(false);
  const [showEditJobTitle, setShowEditJobTitle] = useState(false);
  const [showEditGrade, setShowEditGrade] = useState(false);
  const [showJobTitleDetail, setShowJobTitleDetail] = useState(false);
  const [showGradeDetail, setShowGradeDetail] = useState(false);
  const [selectedJobTitle, setSelectedJobTitle] = useState<JobTitle | null>(null);
  const [selectedGrade, setSelectedGrade] = useState<SalaryGrade | null>(null);

  const departments = [...new Set(mockJobTitles.map((j) => j.department))];

  // Filter data
  const filteredData = useMemo(() => {
    if (activeTab === 'job-titles') {
      return mockJobTitles.filter((item) => {
        const matchesSearch = !searchTerm || item.title.toLowerCase().includes(searchTerm.toLowerCase()) || item.department.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesDept = !departmentFilter || item.department === departmentFilter;
        const matchesStatus = !statusFilter || item.status === statusFilter;
        return matchesSearch && matchesDept && matchesStatus;
      });
    }
    return mockSalaryGrades.filter((item) => {
      const matchesSearch = !searchTerm || item.gradeName.toLowerCase().includes(searchTerm.toLowerCase()) || item.level.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [activeTab, searchTerm, departmentFilter, statusFilter]);

  const tabs = [
    { id: 'job-titles' as const, label: 'Job Titles', icon: <Briefcase className="w-4 h-4" />, count: mockJobTitles.length },
    { id: 'salary-grades' as const, label: 'Salary Grades', icon: <TrendingUp className="w-4 h-4" />, count: mockSalaryGrades.length },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<Briefcase className="w-6 h-6" />}
        title="Job Titles & Grades"
        description="Job classification management is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-purple-500"
        gradientTo="to-violet-500"
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('Job Titles & Grades')}</h1>
            <p className="text-gray-600 mt-2">{t('Manage job classifications and compensation structures')}</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            {activeTab === 'job-titles' ? <Briefcase className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
            <span>
              {filteredData.length} {activeTab === 'job-titles' ? t('job title') : t('salary grade')}{filteredData.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-card rounded-lg shadow-sm border border-border mb-6">
        <div className="border-b border-border">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-border'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200`}
              >
                {tab.icon}
                <span>{t(tab.label)}</span>
                <span className={`${
                  activeTab === tab.id ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
                } inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Search and Filter Controls */}
        <div className="p-6 space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700" />
            <input
              type="text"
              placeholder={t(`Search ${activeTab === 'job-titles' ? 'job titles' : 'salary grades'}...`)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 w-full max-w-md"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {activeTab === 'job-titles' && (
                <select
                  value={departmentFilter}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                  className="border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                >
                  <option value="">{t('All Departments')}</option>
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              )}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              >
                <option value="">{t('All Statuses')}</option>
                <option value="Active">{t('Active')}</option>
                <option value="Draft">{t('Draft')}</option>
                <option value="Inactive">{t('Inactive')}</option>
              </select>
            </div>

            <div className="flex items-center space-x-3">
              <button
                className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg"
               
              >
                <Download className="w-4 h-4" />
                <span>{t('Export')}</span>
              </button>
              <button
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg"
                onClick={() => activeTab === 'job-titles' ? setShowAddJobTitle(true) : setShowAddGrade(true)}
              >
                <Plus className="w-4 h-4" />
                <span>{t('Add')} {activeTab === 'job-titles' ? t('Job Title') : t('Grade')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        {activeTab === 'job-titles' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Job Title')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Department')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Grade')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Employees')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Reports To')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Status')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(filteredData as JobTitle[]).map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => { setSelectedJobTitle(job); setShowJobTitleDetail(true); }}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{job.title}</div>
                        <div className="text-xs text-gray-500 max-w-xs truncate">{job.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.department}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                        {job.grade}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4 text-gray-700" />
                        <span className="text-sm text-gray-900">{job.employeeCount}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{job.reportingTo}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        job.status === 'Active' ? 'bg-green-100 text-green-800' : job.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {t(job.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="text-gray-600" onClick={(e) => { e.stopPropagation(); setSelectedJobTitle(job); setShowJobTitleDetail(true); }}>
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="text-gray-600" onClick={(e) => { e.stopPropagation(); setSelectedJobTitle(job); setShowEditJobTitle(true); }}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="text-gray-600" onClick={(e) => { e.stopPropagation(); console.log('Delete job title', job.id); }}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {(filteredData as SalaryGrade[]).map((grade) => (
              <div key={grade.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{grade.gradeName}</h3>
                      <p className="text-sm text-gray-500">{grade.level}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{t('Salary Range')}</div>
                      <div className="text-sm font-semibold text-gray-900">
                        {grade.salaryRange.currency} {grade.salaryRange.min.toLocaleString()} - {grade.salaryRange.max.toLocaleString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{t('Employees')}</div>
                      <div className="text-sm font-semibold text-gray-900">{grade.employeeCount}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{t('Job Titles')}</div>
                      <div className="text-sm font-semibold text-gray-900">{grade.jobTitleCount}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">{t('Benefits')}</div>
                      <div className="text-sm font-semibold text-gray-900">{grade.benefitsPackage}</div>
                    </div>
                    <button
                      onClick={() => setExpandedGrade(expandedGrade === grade.id ? null : grade.id)}
                      className="p-2 text-gray-700 hover:text-gray-600 transition-colors"
                    >
                      {expandedGrade === grade.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    <div className="flex items-center space-x-2">
                      <button className="text-gray-600" onClick={() => { setSelectedGrade(grade); setShowGradeDetail(true); }}>
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-gray-600" onClick={() => { setSelectedGrade(grade); setShowEditGrade(true); }}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-gray-600" onClick={() => console.log('Delete grade', grade.id)}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                {expandedGrade === grade.id && (
                  <div className="mt-4 ml-16 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('Progression Criteria')}</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {grade.progressionCriteria.map((c, i) => (
                            <li key={i} className="flex items-center space-x-2">
                              <span className="w-1.5 h-1.5 bg-purple-600 rounded-full" />
                              <span>{t(c)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('Eligibility Rules')}</h4>
                        <p className="text-sm text-gray-600">{t(grade.eligibilityRules)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddJobTitleModal
        isOpen={showAddJobTitle}
        onClose={() => setShowAddJobTitle(false)}
        departments={departments}
        grades={mockSalaryGrades.map(g => g.gradeName)}
      />

      <AddGradeModal
        isOpen={showAddGrade}
        onClose={() => setShowAddGrade(false)}
      />

      <EditJobTitleModal
        isOpen={showEditJobTitle}
        onClose={() => { setShowEditJobTitle(false); setSelectedJobTitle(null); }}
        onSubmit={(data) => {
          console.log('Edit job title:', data);
          setShowEditJobTitle(false);
          setSelectedJobTitle(null);
        }}
        jobTitle={selectedJobTitle}
      />

      <EditGradeModal
        isOpen={showEditGrade}
        onClose={() => { setShowEditGrade(false); setSelectedGrade(null); }}
        onSubmit={(data) => {
          console.log('Edit grade:', data);
          setShowEditGrade(false);
          setSelectedGrade(null);
        }}
        grade={selectedGrade}
      />

      <JobTitleDetailModal
        isOpen={showJobTitleDetail}
        onClose={() => { setShowJobTitleDetail(false); setSelectedJobTitle(null); }}
        jobTitle={selectedJobTitle}
        onEdit={() => {
          setShowJobTitleDetail(false);
          setShowEditJobTitle(true);
        }}
      />

      <GradeDetailModal
        isOpen={showGradeDetail}
        onClose={() => { setShowGradeDetail(false); setSelectedGrade(null); }}
        grade={selectedGrade}
        onEdit={() => {
          setShowGradeDetail(false);
          setShowEditGrade(true);
        }}
      />
    </div>
  );
}
